// Admin → Illustrative projects: for each "Materials - Illustrative Projects" example, who may open it (all users -
// the default - or only the users listed here) and whether its projects run with the Advanced plan's resources
// (property calculations, larger fits, longer jobs; default: no). The user's own plan and account quotas (projects,
// storage, jobs per day, compute budget) are not changed. Every change is written to the audit log.
import { Badge, Button, Card, CloseButton, Group, SegmentedControl, Select, Stack, Switch, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ErrorBox, notifyError, notifyOk } from "../../components/Errors";
import { get, put, type AdminUser } from "../../lib/api";

interface AccessUser { id: number; email: string; name: string; status: string }
export interface ExampleAccessRow {
  id: string; order: number; name: string; material: string; all_users: boolean; advanced: boolean;
  users: AccessUser[]; projects: number; product?: "skfitter" | "dft" | "negf" | "properties" | "wannier";
}

const GROUPS: [string, string][] = [
  ["skfitter", "Tight Binding - Illustrative Projects"], ["dft", "DFT - Illustrative Projects"], ["negf", "NEGF - Illustrative Projects"],
  ["wannier", "Tight Binding · Wannier functions - Illustrative Projects"], ["properties", "Properties - Illustrative Projects"],
];

export function ExamplesAccessTab() {
  const rows = useQuery({ queryKey: ["admin-examples"], queryFn: () => get<ExampleAccessRow[]>("/admin/examples") });
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed" maw={900}>
        Who may open each illustrative project (DFT, Tight Binding, Wannier functions, Properties and NEGF), and whether its
        projects run with the <b>Advanced plan's resources</b>: for a Tight Binding project property calculations (Berry
        curvature, AHC, SHC), larger fits and longer jobs; for a Wannier project the Advanced Wannier limits (route A on
        the server, larger meshes and more functions - what most "try this" runs need); for an NEGF project the Advanced NEGF limits (device size, energy and bias points,
        self-consistent size); for that project only. The
        users' own plans and account quotas (projects, storage, jobs per day, compute budget) stay as they are.
        Administrators see every project.
      </Text>
      <ErrorBox error={rows.error} />
      {GROUPS.map(([product, title]) => {
        const group = (rows.data ?? []).filter((r) => (r.product ?? "skfitter") === product);
        if (!group.length) return null;
        return (
          <Stack key={product} gap="xs" data-testid={`example-group-${product}`}>
            <Text fw={700} mt="xs">{title}</Text>
            {group.map((r, i) => <ExampleAccessCard key={r.id} row={r} index={i + 1} />)}
          </Stack>
        );
      })}
    </Stack>
  );
}

function ExampleAccessCard({ row, index }: { row: ExampleAccessRow; index: number }) {
  const qc = useQueryClient();
  const [allUsers, setAllUsers] = useState(row.all_users);
  const [users, setUsers] = useState<AccessUser[]>(row.users);
  const [advanced, setAdvanced] = useState(row.advanced);
  useEffect(() => { setAllUsers(row.all_users); setUsers(row.users); setAdvanced(row.advanced); }, [row]);
  const dirty = allUsers !== row.all_users || advanced !== row.advanced
    || users.map((u) => u.id).join(",") !== row.users.map((u) => u.id).join(",");
  const save = useMutation({
    mutationFn: () => put<ExampleAccessRow>(`/admin/examples/${row.id}`,
      { all_users: allUsers, user_ids: users.map((u) => u.id), advanced }),
    onSuccess: () => { notifyOk(`${row.name}: access saved.`); void qc.invalidateQueries({ queryKey: ["admin-examples"] });
      void qc.invalidateQueries({ queryKey: ["examples"] }); },
    onError: notifyError,
  });
  return (
    <Card withBorder data-testid={`example-access-${row.id}`}>
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap">
          <Text fw={600}>{index}. {row.name} <Text span c="dimmed" fw={400} size="sm">· {row.material}{row.product === "dft" ? " · DFT" : row.product === "negf" ? " · NEGF" : row.product === "properties" ? " · Properties" : row.product === "wannier" ? " · Wannier" : ""}</Text></Text>
          <Text size="xs" c="dimmed">{row.projects} {row.projects === 1 ? "user has" : "users have"} opened it</Text>
        </Group>
        <Group gap="xl" align="flex-start" wrap="wrap">
          <Stack gap={4}>
            <Text size="sm" fw={500}>Access</Text>
            <SegmentedControl size="xs" value={allUsers ? "all" : "selected"} onChange={(v) => setAllUsers(v === "all")}
              data={[{ value: "all", label: "All users" }, { value: "selected", label: "Selected users" }]} />
          </Stack>
          {row.product !== "dft" && <Stack gap={4}>
            <Text size="sm" fw={500}>Allow Advanced plan</Text>
            <Switch checked={advanced} onChange={(e) => setAdvanced(e.currentTarget.checked)} label={advanced ? "Yes" : "No"}
              aria-label={`Allow Advanced plan for ${row.name}`} />
          </Stack>}
        </Group>
        {!allUsers && (
          <Stack gap={6}>
            <Text size="sm" fw={500}>Users with access</Text>
            {users.length === 0 && <Text size="sm" c="orange">Nobody yet - only administrators can open it.</Text>}
            <Group gap={6}>
              {users.map((u) => (
                <Badge key={u.id} variant="light" size="lg" pr={3} tt="none"
                  rightSection={<CloseButton size="xs" aria-label={`Remove ${u.email}`} onClick={() => setUsers(users.filter((x) => x.id !== u.id))} />}>
                  {u.name ? `${u.name} · ${u.email}` : u.email}
                </Badge>
              ))}
            </Group>
            <UserPicker exclude={users.map((u) => u.id)} onPick={(u) => setUsers([...users, u])} label={`Add a user to ${row.name}`} />
          </Stack>
        )}
        {!allUsers && row.all_users && <Text size="xs" c="dimmed">Users who are not listed no longer see this project; a copy
          they opened stays theirs, without Advanced resources.</Text>}
        <Group gap="xs">
          <Button size="xs" onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>Save</Button>
          <Button size="xs" variant="default" disabled={!dirty}
            onClick={() => { setAllUsers(row.all_users); setUsers(row.users); setAdvanced(row.advanced); }}>Reset</Button>
        </Group>
      </Stack>
    </Card>
  );
}

function UserPicker({ exclude, onPick, label }: { exclude: number[]; onPick: (u: AccessUser) => void; label: string }) {
  const [q, setQ] = useState("");
  const [debounced] = useDebouncedValue(q, 250);
  const found = useQuery({
    queryKey: ["admin-users", debounced, ""],
    queryFn: () => get<AdminUser[]>(`/admin/users?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.trim().length >= 2,
  });
  const options = (found.data ?? []).filter((u) => !exclude.includes(u.id)).slice(0, 30);
  return (
    <Select w={360} size="xs" searchable clearable value={null} searchValue={q} onSearchChange={setQ} aria-label={label}
      placeholder="Search users by e-mail, name or organization" nothingFoundMessage={q.trim().length < 2 ? "Type at least 2 letters" : "No users found"}
      data={options.map((u) => ({ value: String(u.id), label: u.name ? `${u.name} · ${u.email}` : u.email }))}
      filter={({ options: o }) => o}
      onChange={(v) => {
        const u = options.find((x) => String(x.id) === v);
        if (u) { onPick({ id: u.id, email: u.email, name: u.name, status: u.status }); setQ(""); }
      }} />
  );
}
